import { useState, useEffect, useCallback } from 'react';
import { useAdminAuth as useAuth0 } from '@/hooks/useAdminAuth';
import { Plus, Trash2, Edit3, Eye, EyeOff, GripVertical, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Partners() {
  const { getAccessTokenSilently } = useAuth0();
  const [items, setItems] = useState([]);
  const [showEdit, setShowEdit] = useState(null); // null | 'new' | partner obj
  const [loading, setLoading] = useState(true);

  const auth = useCallback(async () => {
    const t = await getAccessTokenSilently();
    return { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' };
  }, [getAccessTokenSilently]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const h = await auth();
      const r = await fetch(`${API}/admin/partners`, { headers: h });
      const d = await r.json();
      setItems(d.partners || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  async function save(body, id) {
    const h = await auth();
    if (id) await fetch(`${API}/admin/partners/${id}`, { method: 'PUT', headers: h, body: JSON.stringify(body) });
    else await fetch(`${API}/admin/partners`, { method: 'POST', headers: h, body: JSON.stringify(body) });
    setShowEdit(null);
    load();
  }
  async function del(id) {
    if (!window.confirm('Obriši partnera?')) return;
    const h = await auth();
    await fetch(`${API}/admin/partners/${id}`, { method: 'DELETE', headers: h });
    load();
  }
  async function toggleVisible(p) {
    const h = await auth();
    await fetch(`${API}/admin/partners/${p.id}`, { method: 'PUT', headers: h, body: JSON.stringify({ visible: !p.visible }) });
    load();
  }
  async function moveTo(id, direction) {
    const idx = items.findIndex(i => i.id === id);
    if (idx < 0) return;
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= items.length) return;
    const newOrder = [...items];
    [newOrder[idx], newOrder[newIdx]] = [newOrder[newIdx], newOrder[idx]];
    const h = await auth();
    await fetch(`${API}/admin/partners/reorder`, { method: 'POST', headers: h, body: JSON.stringify({ ids: newOrder.map(x => x.id) }) });
    load();
  }

  return (
    <div className="p-6" data-testid="partners-admin">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-semibold">Partneri</h1>
          <p className="text-xs text-zinc-400 mt-1">Logo, link i redoslijed za sekciju "Naši partneri" u footeru.</p>
        </div>
        <button onClick={() => setShowEdit('new')} className="px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 rounded-md text-white flex items-center gap-1.5" data-testid="partner-add">
          <Plus size={12} /> Dodaj partnera
        </button>
      </div>

      <div className="border border-zinc-800 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900 text-zinc-400 text-left text-xs">
            <tr>
              <th className="px-3 py-2 w-10"></th>
              <th className="px-3 py-2 w-24">Logo</th>
              <th className="px-3 py-2">Naziv</th>
              <th className="px-3 py-2">Web</th>
              <th className="px-3 py-2 w-24">Vidljiv</th>
              <th className="px-3 py-2 text-right">Akcije</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-8 text-zinc-500">Učitavam…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8 text-zinc-500">Nema partnera.</td></tr>
            ) : items.map((p, i) => (
              <tr key={p.id} className="border-t border-zinc-800" data-testid={`partner-row-${p.id}`}>
                <td className="px-3 py-2 text-zinc-500">
                  <div className="flex flex-col gap-0.5">
                    <button onClick={() => moveTo(p.id, -1)} disabled={i === 0} className="text-[10px] disabled:opacity-30 hover:text-white">▲</button>
                    <button onClick={() => moveTo(p.id, 1)} disabled={i === items.length - 1} className="text-[10px] disabled:opacity-30 hover:text-white">▼</button>
                  </div>
                </td>
                <td className="px-3 py-2"><img src={p.logo_url} alt={p.name} className="h-8" /></td>
                <td className="px-3 py-2 font-medium">{p.name}</td>
                <td className="px-3 py-2 text-zinc-400 max-w-xs truncate">{p.website_url}</td>
                <td className="px-3 py-2">
                  <button onClick={() => toggleVisible(p)} className="text-zinc-400 hover:text-white" data-testid={`partner-visible-${p.id}`}>
                    {p.visible ? <Eye size={14} /> : <EyeOff size={14} />}
                  </button>
                </td>
                <td className="px-3 py-2 text-right space-x-1">
                  <button onClick={() => setShowEdit(p)} className="p-1.5 text-zinc-400 hover:text-zinc-200" data-testid={`partner-edit-${p.id}`}><Edit3 size={14} /></button>
                  <button onClick={() => del(p.id)} className="p-1.5 text-red-400 hover:text-red-300" data-testid={`partner-delete-${p.id}`}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showEdit && <EditPartnerModal partner={showEdit === 'new' ? null : showEdit} onClose={() => setShowEdit(null)} onSave={save} />}
    </div>
  );
}

function EditPartnerModal({ partner, onClose, onSave }) {
  const [name, setName] = useState(partner?.name || '');
  const [logo, setLogo] = useState(partner?.logo_url || '');
  const [web, setWeb] = useState(partner?.website_url || '');
  const [visible, setVisible] = useState(partner?.visible ?? true);
  const [maxHeight, setMaxHeight] = useState(partner?.max_height ?? '');
  const [maxWidth, setMaxWidth] = useState(partner?.max_width ?? '');

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="partner-edit-modal">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">{partner ? 'Uredi partnera' : 'Novi partner'}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X size={18} /></button>
        </div>
        <form onSubmit={e => {
            e.preventDefault();
            onSave({
              name, logo_url: logo, website_url: web, visible,
              max_height: maxHeight === '' ? null : parseInt(maxHeight, 10) || null,
              max_width: maxWidth === '' ? null : parseInt(maxWidth, 10) || null,
            }, partner?.id);
          }}>
          <label className="block text-xs text-zinc-400 mb-1">Naziv</label>
          <input value={name} onChange={e => setName(e.target.value)} required className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm mb-3" data-testid="partner-name" />
          <label className="block text-xs text-zinc-400 mb-1">Logo URL</label>
          <input value={logo} onChange={e => setLogo(e.target.value)} required className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm mb-2" data-testid="partner-logo" />
          {logo && (
            <img src={logo} alt="" className="mb-3 bg-white/5 rounded p-1 object-contain"
                 style={{
                   maxHeight: maxHeight ? `${maxHeight}px` : '48px',
                   maxWidth: maxWidth ? `${maxWidth}px` : undefined,
                 }} />
          )}
          <label className="block text-xs text-zinc-400 mb-1">Website URL</label>
          <input value={web} onChange={e => setWeb(e.target.value)} className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm mb-3" data-testid="partner-web" />
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Maks. visina (px)</label>
              <input type="number" min="16" max="240" value={maxHeight}
                     onChange={e => setMaxHeight(e.target.value)}
                     placeholder="npr. 48"
                     className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm"
                     data-testid="partner-max-height" />
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Maks. širina (px)</label>
              <input type="number" min="16" max="400" value={maxWidth}
                     onChange={e => setMaxWidth(e.target.value)}
                     placeholder="npr. 160"
                     className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm"
                     data-testid="partner-max-width" />
            </div>
          </div>
          <p className="text-[11px] text-zinc-500 mb-3">Postavke vrijede za logo u footeru. Ostavi prazno za zadane vrijednosti (h-10/12).</p>
          <label className="flex items-center gap-2 text-sm mb-4">
            <input type="checkbox" checked={visible} onChange={e => setVisible(e.target.checked)} data-testid="partner-visible-input" /> Vidljiv u footeru
          </label>
          <button type="submit" className="w-full py-2 bg-red-600 hover:bg-red-700 rounded-md font-medium" data-testid="partner-save">Spremi</button>
        </form>
      </div>
    </div>
  );
}
